import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useFacility, useSaveFacility } from "@/hooks/api/useFacility";

const FIELDS = ["facilityName", "directorName", "address", "phone", "website"] as const;

export default function FacilitySettings() {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>({});

  const { data: settings, isLoading } = useFacility();
  const saveMutation = useSaveFacility();

  useEffect(() => {
    if (settings) {
      const stringified: Record<string, string> = {};
      for (const key of FIELDS) {
        stringified[key] = typeof settings[key] === "string" ? (settings[key] as string) : "";
      }
      setValues(stringified);
    }
  }, [settings]);

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t("facility.title")}</h1>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <Card className="max-w-lg">
          <CardHeader>
            <CardTitle className="text-base">{t("facility.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveMutation.mutate(values);
              }}
              className="space-y-4"
            >
              {FIELDS.map((field) => (
                <div key={field} className="space-y-2">
                  <Label>{t(`facility.${field}`)}</Label>
                  <Input
                    value={values[field] ?? ""}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [field]: e.target.value }))
                    }
                  />
                </div>
              ))}
              <div className="flex justify-end">
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t("facility.saveSettings")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </Layout>
  );
}
